<template>
  <div class="transcript-section score-panel">
    <template v-if="status === 'ready' && scoreResult">
      <div class="score-header">
        <h4>How close were you?</h4>
      </div>
      <div class="transcript-content">
        <p class="reference-line" @click.self="selectedIdx = null">
          <span
            v-for="(w, i) in scoreResult.refWords"
            :key="i"
            class="ref-word-wrap"
          >
            <span
              class="ref-word"
              :class="['ref-word--' + w.status, { 'ref-word--selected': selectedIdx === i }]"
              @click="w.status !== 'match' ? toggleWord(i, w.word) : null"
            >{{ w.word }}</span>
            <span v-if="selectedIdx === i" class="ipa-tooltip">
              <span class="ipa-tooltip-word">{{ w.word }}</span>
              <span v-if="ipaCache[w.word]" class="ipa-tooltip-ipa">/{{ ipaCache[w.word] }}/</span>
              <span v-else-if="ipaFetching[w.word]" class="ipa-tooltip-loading">…</span>
            </span>
          </span>
        </p>
        <div v-if="recognizedText" class="you-said">
          <span class="you-said-label">You said</span>
          <p class="you-said-text">{{ recognizedText }}</p>
        </div>
        <p class="legend">
          <span class="legend-item legend-item--match">said</span>
          <span class="legend-item legend-item--close">close</span>
          <span class="legend-item legend-item--missed">missed</span>
        </p>
      </div>
    </template>

    <template v-else>
      <h4>Your attempted transcript:</h4>
      <div class="transcript-content">
        <p v-if="recognizedText">{{ recognizedText }}</p>
        <p v-else class="placeholder-text">No speech was recorded in the Learning Screen.</p>
        <p v-if="status === 'loading'" class="status-note">
          <i class="fas fa-circle-notch fa-spin"></i> Preparing your score…
        </p>
        <p v-else-if="status === 'unavailable'" class="status-note">
          Automatic scoring isn't available for this video - compare with the subtitles below.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
var IPA_API = process.env.TRANSCRIPT_API_URL + '/v1/ipa'

export default {
  name: 'ScorePanel',
  props: {
    status: {
      type: String, // 'loading' | 'ready' | 'unavailable'
      default: 'loading'
    },
    scoreResult: {
      type: Object,
      default: null
    },
    recognizedText: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      selectedIdx: null,
      ipaCache: {},    // word → ipa string (non-empty = found, null = not found)
      ipaFetching: {}  // word → true while in-flight
    }
  },
  watch: {
    scoreResult: function () {
      this.selectedIdx = null
    }
  },
  methods: {
    toggleWord: function (idx, word) {
      if (this.selectedIdx === idx) {
        this.selectedIdx = null
        return
      }
      this.selectedIdx = idx
      if (!(word in this.ipaCache) && !this.ipaFetching[word]) {
        this.$set(this.ipaFetching, word, true)
        this.fetchIpa(word)
      }
    },
    fetchIpa: function (word) {
      var self = this
      fetch(IPA_API + '?word=' + encodeURIComponent(word))
        .then(function (res) { return res.json() })
        .then(function (data) {
          self.$set(self.ipaCache, word, data.ipa || null)
        })
        .catch(function () {
          self.$set(self.ipaCache, word, null)
        })
    }
  }
}
</script>

<style scoped>
.score-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-bottom: 5px;
}

.score-header h4 {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}


.reference-line {
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 600;
  line-height: 2.2;
}

.ref-word-wrap {
  display: inline-block;
  position: relative;
}

.ref-word {
  display: inline-block;
  margin-right: 4px;
  padding: 2px 6px;
  line-height: 1.4;
  border-radius: 8px;
}

.ref-word--match {
  color: var(--pass);
  background: var(--pass-soft);
}

.ref-word--close {
  color: var(--warn);
  background: var(--warn-soft);
  cursor: pointer;
}

.ref-word--missed {
  color: var(--fail);
  background: var(--fail-soft);
  text-decoration: underline;
  text-decoration-style: dotted;
  cursor: pointer;
}

.ref-word--close.ref-word--selected,
.ref-word--missed.ref-word--selected {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* IPA tooltip: floats above the tapped word chip */
.ipa-tooltip {
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--surface-2);
  border: 1px solid var(--line-strong);
  border-radius: var(--r-control);
  padding: 6px 10px 7px;
  white-space: nowrap;
  z-index: 20;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.ipa-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: var(--surface-2);
}

.ipa-tooltip-word {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 2px;
}

.ipa-tooltip-ipa {
  display: block;
  font-size: 12px;
  font-style: italic;
  color: var(--accent);
  letter-spacing: 0.04em;
}

.ipa-tooltip-loading {
  display: block;
  font-size: 12px;
  color: var(--muted);
  letter-spacing: 0.1em;
}

.you-said {
  margin-bottom: 6px;
}

.you-said-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--faint);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.you-said-text {
  margin: 2px 0 0 0;
  font-size: 14px;
  color: var(--muted);
  font-style: italic;
}

.legend {
  margin: 0 0 12px;
  font-size: 12px;
  color: var(--faint);
}

.legend-item {
  padding: 1px 7px;
  border-radius: 8px;
  margin-right: 6px;
}

.legend-item--match  { background: var(--pass-soft); color: var(--pass); }
.legend-item--close  { background: var(--warn-soft); color: var(--warn); }
.legend-item--missed { background: var(--fail-soft); color: var(--fail); }

.status-note {
  margin-top: 8px;
  font-size: 13px;
  color: var(--faint);
}

.status-note i {
  margin-right: 4px;
}

.placeholder-text {
  color: var(--faint);
  font-style: italic;
}
</style>
